<template>
	<div class="dashboard-container">
		<CookieModuleLayout>
			<template #content>
				<DashboardFilterAccordion
					v-if="hasDashboards"
					:initial-filters="filter"
					:initial-palette="selectedPalette"
					:current-dashboard-id="filter.dashboardValue"
					:available-brands="brandOptions || []"
					@apply-settings="applyDashboardSettings"
					@dashboard-change="onDashboardChangeFromFilter"
					@colors-updated="onColorsUpdated"
				/>
				<!-- No dashboards available state -->
				<v-row
					v-if="!hasDashboards && !loadingDashboardData"
					justify="center"
					class="mt-8"
				>
					<v-col
						cols="12"
						md="6"
					>
						<v-card class="pa-6 text-center">
							<v-icon
								size="64"
								color="primary"
								class="mb-0"
							>
								mdi-view-dashboard-outline
							</v-icon>
							<h2 class="text-h5 mb-2">
								Consent Banners Dashboard
							</h2>
							<p class="text-body-1 grey--text mb-4">
								Your consent banners dashboard has moved to a dedicated application for an improved experience.
							</p>
							<v-btn
								color="primary"
								@click="goToConsentBannersApp"
							>
								Go to Consent Banners
							</v-btn>
						</v-card>
					</v-col>
				</v-row>
				<!-- Whole-dashboard failure. The per-chart error states cannot
				cover this: every chart row is rendered by a v-for over `charts`,
				which a failed first load leaves empty, so there is no card to
				put an error inside and the page would otherwise show the filter
				bar over blank space. A refetch that fails while charts are
				already on screen keeps them and reports itself by snackbar
				instead, so this only speaks up when there is nothing to keep. -->
				<v-row
					v-else-if="showDashboardError"
					justify="center"
					class="mt-8"
				>
					<v-col
						cols="12"
						md="6"
					>
						<v-card
							class="pa-6 text-center"
							data-testid="dashboard-error"
						>
							<v-icon
								size="64"
								color="error"
								class="mb-2"
							>
								mdi-alert-outline
							</v-icon>
							<h2 class="text-h5 mb-2">
								Couldn't load this dashboard
							</h2>
							<p class="text-body-1 grey--text mb-4">
								Try again, or refresh the page if the problem persists
							</p>
							<v-btn
								color="primary"
								data-testid="dashboard-error-retry"
								@click="getDashboardData"
							>
								<v-icon
									left
									small
								>
									mdi-refresh
								</v-icon>
								Try again
							</v-btn>
						</v-card>
					</v-col>
				</v-row>

				<!-- CONSENT DASHBOARD: Enhanced Layout with KPI Cards and Derived Pies -->
				<template v-else-if="isConsentDashboard">
					<!-- Loading state -->
					<template v-if="loadingDashboardData">
						<v-row class="mb-6">
							<v-col
								v-for="n in 4"
								:key="`kpi-skeleton-${n}`"
								cols="12"
								sm="6"
								lg="3"
							>
								<v-skeleton-loader
									type="card"
									height="100"
								/>
							</v-col>
						</v-row>
						<v-row
							v-for="n in 4"
							:key="`chart-skeleton-${n}`"
							class="mb-0"
							dense
						>
							<v-col cols="12">
								<v-skeleton-loader
									type="card"
									height="300"
								/>
							</v-col>
						</v-row>
					</template>

					<!-- Loaded state -->
					<template v-else>
						<!-- Compact Health Status Cards -->
						<v-row
							v-if="healthChecks && healthChecks.length > 0"
						>
							<v-col
								v-for="healthCheck in healthChecks"
								:key="healthCheck.chartId"
								cols="12"
								sm="6"
								md="4"
								lg="3"
							>
								<div
									class="health-status-card"
									:class="{ 'health-status-card--warning': healthCheck.amberStatus }"
								>
									<v-icon
										small
										:color="healthCheck.amberStatus ? 'warning' : 'success'"
									>
										{{ healthCheck.amberStatus ? 'mdi-alert-circle' : 'mdi-check-circle' }}
									</v-icon>
									<div class="health-status-content">
										<span class="health-status-label">
											{{ healthCheck.title }}
										</span>
										<span class="health-status-value">
											{{ getHealthStatusText(healthCheck) }}
										</span>
									</div>
								</div>
							</v-col>
						</v-row>

						<!-- KPI Cards Row -->
						<v-row
							v-if="dataSubjectMetrics.length > 0 || kpiStats.length > 0"
							class="mt-0 mb-0 kpi-row"
						>
							<v-col
								v-for="metric in dataSubjectMetrics"
								:key="`data-subject-${metric.title}`"
								cols="12"
								sm="6"
								lg="3"
								:xl="totalMetricCardCount <= 4 ? '3' : '2'"
							>
								<div class="metric-card-wrapper">
									<MetricCard
										:title="metric.title"
										:value="formatNumber(metric.value)"
										:subtitle="metric.exactCount"
										:subtitle-tooltip="metric.asOfDate ? `Correct as of ${metric.asOfDate}` : ''"
										:icon="metric.icon"
										icon-color="grey"
									/>
									<v-tooltip
										v-if="metric.hasDetailChart && canExpandDataSubjectChart"
										bottom
									>
										<template #activator="{ on, attrs }">
											<v-btn
												icon
												small
												absolute
												class="metric-expand-btn mb-1"
												aria-label="View trend chart for Current Total Data Subjects"
												v-bind="attrs"
												v-on="on"
												@click="openDataSubjectChartOverlay"
											>
												<v-icon small>
													mdi-arrow-expand
												</v-icon>
											</v-btn>
										</template>
										<span>
											View trend chart
										</span>
									</v-tooltip>
								</div>
							</v-col>
							<v-col
								v-for="stat in kpiStats"
								:key="`kpi-${stat.chartId}`"
								cols="12"
								sm="6"
								lg="3"
								:xl="totalMetricCardCount <= 4 ? '3' : '2'"
							>
								<MetricCard
									:title="stat.title"
									:value="formatNumber(stat.value)"
									:icon="stat.icon"
									icon-color="grey"
								/>
							</v-col>
						</v-row>

						<!-- Chart Rows (full width, pie charts kept in code for future use) -->
						<DashboardRow
							v-for="chart in displayCharts"
							:key="`row-${chart.chartId}`"
							:chart="chart"
							:palette="selectedPalette"
							:color-version="colorVersion"
							:hide-toggle="chart.hideToggle"
							full-width
							class="mb-0"
							:loading="loadingDashboardData"
							:error="chartsError"
							@expand="openChartOverlay"
							@retry="getDashboardData"
						/>
					</template>
				</template>

				<!-- DATA PROCESSING DASHBOARD: Enhanced Layout with KPI Cards -->
				<template v-else-if="isDataProcessingDashboard">
					<!-- Loading state -->
					<template v-if="loadingDashboardData">
						<v-row class="mb-6">
							<v-col
								v-for="n in 4"
								:key="`kpi-skeleton-${n}`"
								cols="12"
								sm="6"
								lg="3"
							>
								<v-skeleton-loader
									type="card"
									height="100"
								/>
							</v-col>
						</v-row>
						<v-row
							v-for="n in 4"
							:key="`chart-skeleton-${n}`"
							class="mb-0"
							dense
						>
							<v-col cols="6">
								<v-skeleton-loader
									type="card"
									height="300"
								/>
							</v-col>
							<v-col cols="6">
								<v-skeleton-loader
									type="card"
									height="300"
								/>
							</v-col>
						</v-row>
					</template>

					<!-- Loaded state -->
					<template v-else>
						<!-- Compact Health Status Cards -->
						<v-row
							v-if="healthChecks && healthChecks.length > 0"
							class="mb-2"
						>
							<v-col
								v-for="healthCheck in healthChecks"
								:key="healthCheck.chartId"
								cols="12"
								sm="6"
								md="4"
								lg="3"
							>
								<div
									class="health-status-card"
									:class="{ 'health-status-card--warning': healthCheck.amberStatus }"
								>
									<v-icon
										small
										:color="healthCheck.amberStatus ? 'warning' : 'success'"
									>
										{{ healthCheck.amberStatus ? 'mdi-alert-circle' : 'mdi-check-circle' }}
									</v-icon>
									<div class="health-status-content">
										<span class="health-status-label">
											{{ healthCheck.title }}
										</span>
										<span class="health-status-value">
											{{ getHealthStatusText(healthCheck) }}
										</span>
									</div>
								</div>
							</v-col>
						</v-row>

						<!-- KPI Cards Row -->
						<v-row
							v-if="dataProcessingKpiStats.length > 0"
							class="mt-0 mb-0"
						>
							<v-col
								v-for="stat in dataProcessingKpiStats"
								:key="`kpi-${stat.chartId}`"
								cols="12"
								sm="6"
								lg="3"
							>
								<MetricCard
									:title="stat.title"
									:value="formatNumber(stat.value)"
									:icon="stat.icon"
									icon-color="grey"
								/>
							</v-col>
						</v-row>

						<!-- Chart Rows (full width) -->
						<DashboardRow
							v-for="chart in dataProcessingCharts"
							:key="`row-${chart.chartId}`"
							:chart="chart"
							:palette="selectedPalette"
							:hide-toggle="chart.hideToggle"
							full-width
							class="mb-0"
							:loading="loadingDashboardData"
							:error="chartsError"
							@expand="openChartOverlay"
							@retry="getDashboardData"
						/>
					</template>
				</template>

				<!-- OTHER DASHBOARDS: Keep Existing Layout -->
				<template v-else>
					<!-- Compact Health Status Cards -->
					<v-row
						v-if="healthChecks && healthChecks.length > 0"
					>
						<v-col
							v-for="healthCheck in healthChecks"
							:key="healthCheck.chartId"
							cols="12"
							sm="6"
							md="4"
							lg="3"
						>
							<div
								class="health-status-card"
								:class="{ 'health-status-card--warning': healthCheck.amberStatus }"
							>
								<v-icon
									small
									:color="healthCheck.amberStatus ? 'warning' : 'success'"
								>
									{{ healthCheck.amberStatus ? 'mdi-alert-circle' : 'mdi-check-circle' }}
								</v-icon>
								<div class="health-status-content">
									<span class="health-status-label">
										{{ healthCheck.title }}
									</span>
									<span class="health-status-value">
										{{ getHealthStatusText(healthCheck) }}
									</span>
								</div>
							</div>
						</v-col>
					</v-row>
					<v-row>
						<v-col
							v-for="chart in charts"
							:key="`chart-${chart.chartId}-v${colorVersion}`"
							cols="6"
						>
							<CookieModuleDynamicChart
								v-if="chart.chartTypeId !== HEALTH_CHART_TYPE"
								:chart="chart"
								:palette="selectedPalette"
								:loading="loadingDashboardData"
								:error="chartsError"
								@retry="getDashboardData"
							/>
						</v-col>
					</v-row>
				</template>
			</template>
			<template #footer />
		</CookieModuleLayout>

		<!-- Chart Detail Overlay (must be outside CookieModuleLayout which has no default slot) -->
		<ChartDetailOverlay
			:visible="overlayVisible"
			:chart="selectedChart"
			:title="selectedChart ? selectedChart.title : ''"
			:palette="selectedPalette"
			:color-version="colorVersion"
			:filters="filter"
			:available-brands="brandOptions || []"
			:loading="loadingDashboardData"
			:error="chartsError"
			@close="closeChartOverlay"
			@filter-change="onOverlayFilterChange"
			@retry="getDashboardData"
		/>

		<!-- Data Subject Chart Overlay -->
		<DataSubjectChartOverlay
			v-if="dataSubjectChartData"
			:visible="dataSubjectChartOverlayVisible"
			:chart-data="dataSubjectChartData"
			:chart-options="dataSubjectChartOptions"
			@close="closeDataSubjectChartOverlay"
		/>
	</div>
</template>

<script>
/* eslint-disable max-lines */
import { sub, formatISO, differenceInDays, format as formatDate } from 'date-fns'
import { mapGetters, mapActions } from 'vuex'
import CookieModuleLayout from './cookie-module-layout.vue'
import CookieModuleDynamicChart from './cookie-module-charts/cookie-module-dynamic-chart.vue'
import DashboardFilterAccordion from './dashboard-filter-accordion.vue'
import { Periods } from './cookie-module-constants.js'
import { getDashboardChartsData as getDashboardChartsWithData, HEALTH_CHART_TYPE } from '../../../../../shared/utils/api/cookie-module.js'
import { getDataSubjectCounts } from '../../../../../shared/utils/api/consent-analytics.js'
import { DATE_FORMAT } from '../../../../../shared/utils/date-formatting.js'
import { getChartColorByIndex } from '../../../../../shared/utils/getChartColorByIndex.js'
import { chartColorVersion } from '../../../../../shared/state/chart-colors.js'
import { brandOptions, waitForLoad } from '../../../../../shared/state/brands.js'
import { navigateToDashboardPortal, NAV_MODE_EMBEDDED } from '../../../../../shared/utils/navigation/dashboard-navigation.js'
import { showSnackbar } from '../../../../../shared/state/snackbar.js'
import { MetricCard } from '../../component-library-wrapper.js'
import DashboardRow from './dashboard-row.vue'
import { formatNumber } from '../../../../../shared/utils/format-number.js'
import { buildDataSubjectMetrics } from './utils/data-subject-metrics.js'
import { hasPlottableData } from '../../../../../shared/utils/chart-data-state.js'
import ChartDetailOverlay from './chart-detail-overlay.vue'
import DataSubjectChartOverlay from './data-subject-chart-overlay.vue'

// Constants for Dashboard IDs and KPI Configuration
const CONSENT_DASHBOARD_ID = 3
const DATA_PROCESSING_DASHBOARD_ID = 1
const CONSENT_KPI_CONFIG = [
	{ title: 'New Data Subjects', icon: 'mdi-account-plus' },
	{ title: 'Parent Channel Opt-Ins', icon: 'mdi-check-circle' },
	{ title: 'Preference Updates', icon: 'mdi-cog' }
]
// Icons for Data Processing KPIs - uses chart titles from API
const DATA_PROCESSING_KPI_ICONS = [
	'mdi-file-document-outline',
	'mdi-swap-horizontal',
	'mdi-check-circle-outline',
	'mdi-clock-outline'
]

export default {
	components: {
		CookieModuleLayout,
		CookieModuleDynamicChart,
		DashboardFilterAccordion,
		MetricCard,
		DashboardRow,
		ChartDetailOverlay,
		DataSubjectChartOverlay
	},
	setup () {
		return {
			getDashboardChartsWithData,
			getDataSubjectCounts,
			HEALTH_CHART_TYPE,
			Periods,
			brandOptions,
			waitForLoad,
			CONSENT_DASHBOARD_ID,
			DATA_PROCESSING_DASHBOARD_ID,
			CONSENT_KPI_CONFIG,
			DATA_PROCESSING_KPI_ICONS,
			chartColorVersion
		}
	},
	data () {
		// Use inline date calculation and default period value to avoid race conditions
		const now = new Date()
		const oneWeekAgo = sub(now, { weeks: 1 })
		const defaultPeriod = Periods?.LastMonth ?? 2

		return {
			loadingDashboardData: true,
			// Surfaced to the charts so a failed fetch shows their error state
			// instead of being indistinguishable from having no data. Was
			// previously swallowed by a bare console.error.
			dashboardError: null,
			charts: [],
			filter: {
				widgetValue: -1,
				periodValue: defaultPeriod,
				brandId: null,
				dashboardValue: null, // Initialize as null
				startDate: oneWeekAgo.toISOString().split('T')[0],
				endDate: now.toISOString().split('T')[0],
				interval: 0,
				domainValue: 'All'
			},
			selectedPalette: 'Default', // Use a simple default value
			APP_DEFAULT_PERIOD: defaultPeriod,
			APP_DEFAULT_BRAND_ID: null,
			overlayVisible: false,
			selectedChart: null,
			selectedChartId: null,
			selectedChartTitle: null,
			dataSubjectCounts: null,
			dataSubjectChartOverlayVisible: false,
			colorVersion: 0
		}
	},
	computed: {
		...mapGetters('dashboards', ['dashboards']),
		...mapGetters('auth', ['productAreaPermission', 'userDefaultDashboardId']),

		/**
		 * The error handed to the charts. Withheld while good data is still on
		 * screen: `resolveChartState` puts error above populated, so passing a
		 * refetch failure straight through would blank a dashboard full of
		 * working charts on any transient blip. That is worse than leaving the
		 * last-known-good view up, so a refetch failure is announced by
		 * snackbar instead. A first-load failure has no data to preserve, so it
		 * surfaces properly as error cards with a retry.
		 */
		/**
		 * Whether the whole dashboard failed with nothing to show. The
		 * per-chart error states only reach the user through a chart card, and
		 * every card comes from a v-for over `charts`, which a failed first
		 * load leaves empty. Without this the page renders the filter bar over
		 * blank space, with no message and no way to retry.
		 *
		 * @returns {boolean} True if the dashboard-level error state should show
		 */
		showDashboardError () {
			return !!this.dashboardError &&
				!this.loadingDashboardData &&
				this.charts.length === 0
		},

		chartsError () {
			return this.charts.length > 0 ? null : this.dashboardError
		},

		dashboardItems () {
			const items = (this.dashboards || []).map(({ id, name }) => ({ id, name }))
			return items
		},
		hasDashboards () {
			return this.dashboardItems.length > 0
		},
		totalMetricCardCount () {
			return this.dataSubjectMetrics.length + this.kpiStats.length
		},
		healthChecks () {
			return this.charts ? this.charts.filter(c => c.chartTypeId === HEALTH_CHART_TYPE) : []
		},
		isConsentDashboard () {
			return parseInt(this.filter?.dashboardValue) === CONSENT_DASHBOARD_ID
		},
		isDataProcessingDashboard () {
			return parseInt(this.filter?.dashboardValue) === DATA_PROCESSING_DASHBOARD_ID
		},
		kpiStats () {
			if (!this.isConsentDashboard || !this.charts?.length) return []

			// Filter out health check charts first (same as displayCharts)
			const dataCharts = this.charts.filter(chart => chart.chartTypeId !== HEALTH_CHART_TYPE)

			// Select specific charts for KPIs:
			// Index 0: New Data Subjects
			// Index 1: Parent Channel Opt-Ins
			// Index 3: Preference Updates (last chart, skipping Data Requests at index 2)
			const kpiChartIndices = [0, 1, 3]

			return kpiChartIndices
				.filter(chartIndex => dataCharts[chartIndex]) // Ensure chart exists
				.map((chartIndex, kpiIndex) => {
					const chart = dataCharts[chartIndex]

					// Filter out anonymous datasets for the first chart (New Data Subjects)
					const datasets = chartIndex === 0
						? (chart.chartData?.datasets || []).filter(dataset =>
							!dataset.label?.toLowerCase().includes('anonymous'))
						: (chart.chartData?.datasets || [])

					const total = datasets.reduce((acc, dataset) => {
						const datasetSum = dataset.data?.reduce((sum, val) => sum + (val || 0), 0) || 0
						return acc + datasetSum
					}, 0) || 0

					const config = CONSENT_KPI_CONFIG[kpiIndex] || {
						title: chart.title,
						icon: 'mdi-chart-line',
						color: '#666666'
					}

					return {
						...config,
						value: total,
						chartId: chart.chartId
					}
				})
		},
		dataProcessingKpiStats () {
			if (!this.isDataProcessingDashboard || !this.charts?.length) return []

			// Filter out health check charts and take first 4
			let displayableCharts = this.charts.filter(c => c.chartTypeId !== HEALTH_CHART_TYPE).slice(0, 4)

			// Swap second and third charts (index 1 and 2) to match chart display order
			if (displayableCharts.length >= 3) {
				displayableCharts = [...displayableCharts]
				const temp = displayableCharts[1]
				displayableCharts[1] = displayableCharts[2]
				displayableCharts[2] = temp
			}

			return displayableCharts.map((chart, index) => {
				const total = chart.chartData?.datasets?.reduce((acc, dataset) => {
					const datasetSum = dataset.data?.reduce((sum, val) => sum + (val || 0), 0) || 0
					return acc + datasetSum
				}, 0) || 0

				// Shorten verbose titles and fix naming for better card display
				const shortenedTitle = (chart.title || '')
					.replace(/^Connector Services\s+/i, '')
					.replace(/^Failed API Requests$/i, 'Failed Submissions')
					.trim()

				return {
					title: shortenedTitle,
					icon: DATA_PROCESSING_KPI_ICONS[index] || 'mdi-chart-line',
					value: total,
					chartId: chart.chartId
				}
			})
		},
		// Charts excluding health checks (for Consent dashboard display)
		displayCharts () {
			const charts = this.charts?.filter(chart => chart.chartTypeId !== HEALTH_CHART_TYPE) || []

			// Override chart titles for Consent Dashboard to match NextJS hardcoded titles
			if (this.isConsentDashboard) {
				const CHART_TITLES = [
					'New Data Subjects',
					'Parent Channel Opt-Ins',
					'Data Requests',
					'Preference Updates'
				]
				return charts.map((chart, index) => {
					const mappedChart = {
						...chart,
						title: CHART_TITLES[index] || chart.title
					}

					// For the first chart (New Data Subjects), filter out anonymous datasets
					if (index === 0 && chart.chartData?.datasets) {
						const filteredDatasets = chart.chartData.datasets.filter(dataset =>
							!dataset.label?.toLowerCase().includes('anonymous'))

						mappedChart.chartData = {
							...chart.chartData,
							datasets: filteredDatasets
						}

						// Disable legend since there will only be one dataset
						mappedChart.chartOptions = {
							...(chart.chartOptions || {}),
							plugins: {
								...(chart.chartOptions?.plugins || {}),
								legend: {
									...(chart.chartOptions?.plugins?.legend || {}),
									display: false
								}
							}
						}

						// Hide toggle for single-dataset chart
						mappedChart.hideToggle = true
					}

					return mappedChart
				})
			}

			return charts
		},
		// Charts excluding health checks (for Data Processing dashboard display)
		dataProcessingCharts () {
			const charts = this.charts?.filter(chart => chart.chartTypeId !== HEALTH_CHART_TYPE) || []
			// Swap second and third charts (index 1 and 2)
			if (charts.length >= 3) {
				const reordered = [...charts]
				const temp = reordered[1]
				reordered[1] = reordered[2]
				reordered[2] = temp
				return reordered
			}
			return charts
		},
		// Data Subject counts for MetricCards
		dataSubjectMetrics () {
			return buildDataSubjectMetrics(this.dataSubjectCounts, {
				formatNumber,
				formatDate,
				dateFormat: DATE_FORMAT
			})
		},
		// Time series chart data for total data subjects
		dataSubjectChartData () {
			// Reference colorVersion to trigger recalculation when colors change
			// eslint-disable-next-line no-unused-vars
			const _colorVersion = this.colorVersion

			if (!this.dataSubjectCounts?.timeSeries?.length) return null

			const timeSeries = this.dataSubjectCounts.timeSeries

			const primaryColor = getChartColorByIndex(0)
			return {
				labels: timeSeries.map(item => new Date(item.runFinish_UTC)),
				datasets: [
					{
						label: 'Total Data Subjects',
						data: timeSeries.map(item => item.identifiedCount || 0),
						backgroundColor: primaryColor + '40', // Add 25% opacity for gradient fill
						borderColor: primaryColor,
						fill: true, // Enable fill under the line
						tension: 0 // No smoothing - show accurate data points
					}
				]
			}
		},
		/**
		 * Whether the metric card should offer its trend chart. The overlay is
		 * already guarded by `v-if="dataSubjectChartData"`, so without this the
		 * button renders and then does nothing when the series is absent.
		 *
		 * Uses the same predicate as the charts, so the affordance and the
		 * chart behind it cannot disagree about whether there is data.
		 *
		 * @returns {boolean} True if the trend-chart button should be shown
		 */
		canExpandDataSubjectChart () {
			if (this.loadingDashboardData) return false
			return hasPlottableData(this.dataSubjectChartData)
		},

		// Chart options for data subject chart
		dataSubjectChartOptions () {
			return {
				responsive: true,
				maintainAspectRatio: false,
				scales: {
					y: {
						beginAtZero: false
					}
				},
				plugins: {
					legend: {
						display: false
					}
				}
			}
		}
	},
	watch: {
		// Watch for chart color changes from preferences
		chartColorVersion () {
			this.onColorsUpdated()
		}
	},
	async created () {
		await this.getDashboards({ addCookieModule: false, submissionFragmentsEnabled: false })

		await waitForLoad() // Ensure brandOptions are loaded

		// Determine initial dashboard ID - prefer user's default if valid
		let initialDashboardId = null
		if (this.dashboardItems.length > 0) {
			// Check if user has a default dashboard and it exists in available dashboards
			const userDefault = this.userDefaultDashboardId
			const userDefaultExists = userDefault != null && this.dashboardItems.some(d => d.id === userDefault)

			if (userDefaultExists) {
				initialDashboardId = userDefault
			} else {
				// Fall back to first available dashboard
				initialDashboardId = this.dashboardItems[0].id
			}
		}

		if (initialDashboardId === null) {
			this.loadingDashboardData = false
			return
		}

		// Apply default settings
		const appDefaults = this.getApplicationDefaultSettings(initialDashboardId)
		this.filter = { ...appDefaults.filters }
		this.selectedPalette = appDefaults.paletteName

		this.fetchDashboardsAndWidgets()
	},
	async mounted () {
		await this.$nextTick()
		// this.defaultBrandInSelect() // Removed this line
	},
	methods: {
		...mapActions('dashboards', ['getDashboards']),
		formatNumber (value) {
			return formatNumber(value, true)
		},
		/**
		 * Get health status display text
		 * @param {Object} healthCheck - Health check chart object
		 * @returns {string} Status text
		 */
		getHealthStatusText (healthCheck) {
			if (healthCheck.amberStatus === true) return 'Attention'
			return 'OK'
		},
		/**
		 * Get health status icon
		 * @param {Object} healthCheck - Health check chart object
		 * @returns {string} MDI icon name
		 */
		getHealthStatusIcon (healthCheck) {
			if (healthCheck.amberStatus === true) return 'mdi-alert-circle'
			return 'mdi-check-circle'
		},
		/**
		 * Get health status color for icon and border
		 * @param {Object} healthCheck - Health check chart object
		 * @returns {string} Color name
		 */
		getHealthStatusColor (healthCheck) {
			if (healthCheck.amberStatus === true) return 'warning'
			return 'success'
		},
		async goToConsentBannersApp () {
			try {
				// Entered from inside the admin portal's consent dashboard ->
				// embedded: keep the admin navbar in the new app (AB#25457).
				await navigateToDashboardPortal(NAV_MODE_EMBEDDED)
			} catch (err) {
				console.error('[consent-banners] redirect to dashboard portal failed:', err)
				showSnackbar({ color: 'red', text: 'Unable to redirect to Consent Banners. Please try again.' })
			}
		},
		fetchDashboardsAndWidgets () {
			// Use custom dates if the period is Custom, otherwise use calculated dates
			const useCustomDates = this.filter.periodValue === Periods.Custom
			this.getDashboardData(useCustomDates)
		},
		/**
		 * Returns the appropriate Material Design icon for each dashboard type
		 * @param {number} dashboardId - The ID of the dashboard
		 * @returns {string} The icon name for Vuetify v-icon
		 */
		getDashboardIcon (dashboardId) {
			switch (dashboardId) {
				case 0: // Cookie Module (legacy)
					return 'mdi-shield-check-outline'
				case 1: // Data Processing
					return 'mdi-swap-horizontal'
				case 3: // Consent Management
					return 'mdi-account-check-outline'
				case 4: // Associated Cookie Submissions
					return 'mdi-file-document-multiple'
				case 5: // Cookie Fragments
					return 'mdi-puzzle'
				default: // Other custom dashboards
					return 'mdi-chart-line'
			}
		},
		getApplicationDefaultSettings (defaultDashboardIdToUse) {
			const defaultPaletteName = 'Default'
			return {
				filters: {
					periodValue: this.APP_DEFAULT_PERIOD,
					widgetValue: -1, // -1 represents 'All Widgets'
					brandId: this.APP_DEFAULT_BRAND_ID,
					dashboardValue: defaultDashboardIdToUse,
					startDate: null, // Or calculated default like APP_DEFAULT_PERIOD implies
					endDate: null, // Or calculated default
					domainValue: 'All', // Or null
					interval: 0 // Default interval
				},
				paletteName: defaultPaletteName
			}
		},
		applyDashboardSettings ({ filters, palette }) {
			// Store previous values to detect what changed
			const prevFilters = JSON.stringify(this.filter)

			// Update state
			this.filter = { ...filters }
			this.selectedPalette = palette

			// Only refetch data if filters changed (data-affecting changes)
			// Palette is a visual-only change that doesn't require data refetch
			const filtersChanged = JSON.stringify(this.filter) !== prevFilters
			if (filtersChanged) {
				this.fetchDashboardsAndWidgets()
			}
		},
		getIsoDate (date) {
			return formatISO(date, { representation: 'date' })
		},
		getDashboardData (customDates = true) {
			const params = this.getDashboardDataParams(this.filter, customDates)
			this.makeDashboardDataRemoteRequest(params)
		},
		getDashboardDataParams (dashboardFilter, customDates = true) {
			const interval = {
				day: 0,
				week: 1,
				month: 2
			}
			const getInterval = filter => {
				switch (filter.periodValue) {
					case Periods.LastWeek:
					case Periods.LastMonth:
					case Periods.Custom:
						return interval.day
					case Periods.Last3Months:
					case Periods.Last6Months:
						return interval.week
					case Periods.Last12Months:
					default:
						return interval.month
				}
			}
			if (customDates) {
				const daysDifference = differenceInDays(new Date(this.filter.endDate), new Date(this.filter.startDate))
				if (daysDifference <= 31) {
					this.filter.interval = 0
				} else if (daysDifference >= 32 && daysDifference <= 183) {
					this.filter.interval = 1
				} else {
					this.filter.interval = 2
				}
			} else {
				// Only recalculate dates if it's not a custom period
				if (dashboardFilter.periodValue !== Periods.Custom) {
					let duration
					switch (dashboardFilter.periodValue) {
						case Periods.LastWeek:
							duration = { weeks: 1 }
							break
						case Periods.LastMonth:
							duration = { months: 1 }
							break
						case Periods.Last3Months:
							duration = { months: 3 }
							break
						case Periods.Last6Months:
							duration = { months: 6 }
							break
						case Periods.Last12Months:
						default:
							duration = { years: 1 }
					}
					this.filter.endDate = this.getIsoDate(new Date())
					this.filter.startDate = this.getIsoDate(sub(new Date(), duration))
				}
				// For Custom period, keep the existing startDate and endDate
			}
			return {
				startDate: dashboardFilter.startDate,
				endDate: dashboardFilter.endDate,
				interval: customDates ? parseInt(dashboardFilter.interval, 10) : getInterval(dashboardFilter),
				formId: dashboardFilter.widgetValue === null ? -1 : parseInt(dashboardFilter.widgetValue || 0, 10),
				// Handle brandId conversion with proper integer parsing
				brandId: dashboardFilter.brandId === null && this.brandOptions && this.brandOptions.value && this.brandOptions.value.length === 0
					? 0
					: parseInt(dashboardFilter.brandId || 0, 10),
				dashboardId: parseInt(dashboardFilter.dashboardValue || 0, 10),
				domain: dashboardFilter.domainValue
			}
		},
		async makeDashboardDataRemoteRequest (params) {
			this.loadingDashboardData = true
			this.dashboardError = null
			try {
				this.charts = await getDashboardChartsWithData(params)

				// Fetch data subject counts for Consent dashboard
				if (this.isConsentDashboard) {
					await this.fetchDataSubjectCounts()
				}

				// Update selectedChart if overlay is open (for filter changes in overlay)
				if (this.overlayVisible && this.selectedChartId && this.charts) {
					// Find chart by ID (more reliable than title which may be overridden)
					const chartIndex = this.charts.findIndex(c => c.chartId === this.selectedChartId)
					if (chartIndex !== -1) {
						let updatedChart = { ...this.charts[chartIndex] }

						// Apply title override for Consent Dashboard (same logic as displayCharts)
						if (this.isConsentDashboard) {
							const CHART_TITLES = [
								'New Data Subjects',
								'Parent Channel Opt-Ins',
								'Data Requests',
								'Preference Updates'
							]
							// Find the index in non-health charts
							const nonHealthCharts = this.charts.filter(c => c.chartTypeId !== HEALTH_CHART_TYPE)
							const displayIndex = nonHealthCharts.findIndex(c => c.chartId === this.selectedChartId)
							if (displayIndex !== -1 && CHART_TITLES[displayIndex]) {
								updatedChart = { ...updatedChart, title: CHART_TITLES[displayIndex] }
							}
						}

						this.selectedChart = updatedChart
					}
				}
			} catch (err) {
				console.error('Error making dashboard data remote request:', err)
				this.dashboardError = err
				// A failed refetch keeps the last-known-good charts on screen
				// (see chartsError), so the failure has to be announced some
				// other way or it goes unnoticed.
				if (this.charts.length > 0) {
					showSnackbar({ color: 'red', text: 'Unable to refresh dashboard data. Showing the last loaded results.' })
				}
			} finally {
				this.loadingDashboardData = false
			}
		},

		/**
		 * Fetch data subject counts for consent analytics
		 */
		async fetchDataSubjectCounts () {
			try {
				const toDate = this.getIsoDate(new Date())
				const fromDate = this.getIsoDate(sub(new Date(), { months: 12 }))
				this.dataSubjectCounts = await getDataSubjectCounts({
					fromDate,
					toDate
				})
			} catch (err) {
				console.error('Error fetching data subject counts:', err)
				showSnackbar({ color: 'red', text: 'Failed to load data subject counts' })
				this.dataSubjectCounts = null
			}
		},

		/**
		 * Handle dashboard change from filter component
		 * @param {number} dashboardId - The ID of the selected dashboard
		 */
		onDashboardChangeFromFilter (dashboardId) {
			// Ensure dashboard ID is an integer
			const numericDashboardId = parseInt(dashboardId, 10)

			const previousDashboardValue = this.filter.dashboardValue

			// Check if the dashboard exists
			const dashboardExists = this.dashboardItems.some(item => item.id === numericDashboardId)

			if (!dashboardExists) {
				console.error(`Dashboard ID ${numericDashboardId} not found in available dashboards!`)
				return
			}

			// Update the filter dashboard value
			this.filter.dashboardValue = numericDashboardId

			// Trigger API call when dashboard changes
			if (previousDashboardValue !== numericDashboardId) {
				this.fetchDashboardsAndWidgets()
			}
		},

		/**
		 * Open the chart detail overlay
		 * @param {Object} chart - The chart data to display
		 */
		openChartOverlay (chart) {
			this.selectedChart = chart
			this.selectedChartId = chart ? chart.chartId : null
			this.overlayVisible = true
		},

		/**
		 * Close the chart detail overlay
		 */
		closeChartOverlay () {
			this.overlayVisible = false
			this.selectedChart = null
			this.selectedChartId = null
		},

		/**
		 * Handle filter change from overlay
		 * @param {Object} updatedFilters - Updated filter object
		 */
		onOverlayFilterChange (updatedFilters) {
			// Update the filter state
			this.filter = { ...updatedFilters }

			// Fetch new data with updated filters
			this.fetchDashboardsAndWidgets()
		},

		/**
		 * Open the data subject chart overlay
		 */
		openDataSubjectChartOverlay () {
			this.dataSubjectChartOverlayVisible = true
		},

		/**
		 * Close the data subject chart overlay
		 */
		closeDataSubjectChartOverlay () {
			this.dataSubjectChartOverlayVisible = false
		},

		/**
		 * Handle chart colors update from palette dialog
		 * Forces chart re-render by incrementing a key
		 */
		onColorsUpdated () {
			// Increment colorVersion to force chart components to re-render with new colors
			this.colorVersion++
		}
	}
}
</script>

<style scoped>
/* Dashboard container - ensures proper isolation of overlay from main content */
.dashboard-container {
	position: relative;
	width: 100%;
}

/* Ensure proper spacing between dashboard sections */
:deep(.v-row) {
	margin-bottom: 0;
	max-width: 100%;
}

:deep(.v-row.mb-2) {
	/* margin-bottom: 8px !important; */
}

/* Reduce spacing between page header and filter accordion */
:deep(.page-header) {
	margin-bottom: 4px !important;
}

/* Tighter column padding for compact layout */
:deep(.v-col) {
	padding: 6px;
}

/* Compact Health Status Cards - matches MetricCard styling but narrower */
.health-status-card {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	background-color: var(--global-card-background, #ffffff);
	border-radius: var(--radius-lg, 0.75rem);
	border: none;
	border-left: 4px solid var(--global-color-green, #16a34a);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08);
}

.health-status-card--warning {
	border-left-color: var(--global-color-orange-darken-3, #d97706);
}

.health-status-content {
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: space-between;
	gap: 6px;
}

.health-status-label {
	color: var(--global-muted-foreground);
	font-size: 0.8125rem;
	font-weight: 500;
}

.health-status-value {
	color: var(--global-color-green, #16a34a);
	font-size: 0.8125rem;
	font-weight: 600;
}

.health-status-card--warning .health-status-value {
	color: var(--global-color-orange-darken-3, #d97706);
}

/* KPI columns: flex column so cards stretch to equal row height */
:deep(.kpi-row > .col) {
	display: flex;
	flex-direction: column;
}

/* Metric card wrapper for positioning trend chart button */
.metric-card-wrapper {
	position: relative;
	flex: 1;
	display: flex;
	flex-direction: column;
}

/* Ensure all metric cards in a row share the same height */
:deep(.kpi-row .metric-card) {
	flex: 1;
}

/* Expand button for metric cards */
.metric-expand-btn {
	bottom: 12px;
	right: 8px;
}
</style>
